import { useQuery } from "@tanstack/react-query";
import { NavLink, useLocation } from "react-router-dom";

import { modeOf } from "../../lib/meetings-view";
import { queryKeys } from "../../lib/queryKeys";
import { salesApi } from "../../lib/sales-api";
import { NotificationBell } from "../NotificationBell";
import { cn } from "../ui/cn";

/**
 * The diary's navigation.
 *
 * The same shape as the other three products' sidebars -- somebody switching
 * between them twenty times a day should not have to re-learn where things are.
 *
 * The counts are deliberately **yours**, not the desk's. This is the one place
 * in Pulse where that is true, and it is the whole reason Meetings is its own
 * product rather than a section of Sales calls: a rep reading this in a van
 * wants to know what their own afternoon looks like, and a badge counting
 * everybody's visits is a number they cannot act on. Whose diary you are
 * looking at is a filter on the list; whose diary the *sidebar* counts is
 * always yours.
 */
export function MeetingsSidebar({
  connected,
  userId,
}: {
  connected: boolean;
  userId: number;
}) {
  const diary = useQuery({
    queryKey: queryKeys.salesMeetingCounts,
    queryFn: salesApi.meetingCounts,
    staleTime: 30_000,
  });

  return (
    <nav className="flex w-60 shrink-0 flex-col overflow-y-auto border-r border-line bg-app pb-3">
      <div className="flex items-center gap-2 px-4 py-4">
        <span className="text-sm font-semibold text-content">Meetings</span>
        <span
          title={connected ? "Live updates connected" : "Reconnecting…"}
          className={cn(
            "size-2 shrink-0 rounded-full",
            connected ? "bg-sla-ok" : "bg-sla-warning",
          )}
        />
        <span className="ml-auto">
          <NotificationBell />
        </span>
      </div>

      <div className="px-4 pb-1 text-[11px] font-medium uppercase tracking-wide text-faint">
        Your diary
      </div>
      <ul className="px-2">
        {/*
          The calendar's three views by name, as Google's own sidebar has
          them, rather than "Today" and "Upcoming": which window of the diary
          you are looking at is the thing this list switches. Each opens on
          today. The count on Day is today's meetings, which is what a rep
          glancing at it wants to know.
        */}
        <MeetingLink userId={userId} view="recent" label="Recent" />
        <MeetingLink userId={userId} view="day" label="Day" count={diary.data?.today} />
        <MeetingLink userId={userId} view="week" label="Week" />
        <MeetingLink userId={userId} view="month" label="Month" />
      </ul>

      <div className="mt-4 px-4 pb-1 text-[11px] font-medium uppercase tracking-wide text-faint">
        Outstanding
      </div>
      <ul className="px-2">
        {/*
          The one count here worth pulling the eye, and the diary's equivalent
          of the calls list's "Needs attention": the visit happened, so the work
          happened, and nothing about it has reached the account's history. Left
          alone for a fortnight, that is where a pipeline quietly stops
          reflecting reality.
        */}
        <MeetingLink
          userId={userId}
          view="recent"
          filter="needs_notes"
          label="Needs notes"
          count={diary.data?.needsNotes}
          highlight={(diary.data?.needsNotes ?? 0) > 0}
        />
        <MeetingLink
          userId={userId}
          view="recent"
          filter="needs_follow_up"
          label="Needs follow-up"
        />
      </ul>
    </nav>
  );
}

/**
 * A nav link to one view of the diary.
 *
 * Lit by the view actually on screen -- including the remembered one when the
 * URL names none -- and by the feed's filter, but not by the date or whose
 * diary: stepping to next week is still the week view, and the sidebar should
 * keep saying so. `NavLink`'s own matching compares paths only, and every link
 * here is `/meetings`, so it would light all of them at once.
 */
function MeetingLink({
  userId,
  view,
  filter,
  label,
  count,
  highlight,
}: {
  userId: number;
  view: "recent" | "day" | "week" | "month";
  filter?: string;
  label: string;
  count?: number;
  highlight?: boolean;
}) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const active =
    location.pathname.startsWith("/meetings") &&
    modeOf(params, userId) === view &&
    (params.get("filter") ?? undefined) === filter;

  /* Whose diary carries over; the date does not -- a view opens on today. */
  const next = new URLSearchParams({ view });
  if (filter) next.set("filter", filter);
  const who = params.get("who");
  if (who) next.set("who", who);

  return (
    <li>
      <NavLink
        to={`/meetings?${next.toString()}`}
        className={cn(
          "flex items-center gap-2 rounded-md px-2 py-1.5 text-sm",
          active
            ? "bg-surface-2 font-medium text-content"
            : "text-muted hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400",
        )}
      >
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {count !== undefined && count > 0 ? (
          <span
            className={cn(
              "shrink-0 tabular-nums",
              highlight
                ? "rounded-full bg-red-600 px-1.5 text-[10px] font-medium text-white"
                : "text-[11px] text-faint",
            )}
          >
            {count}
          </span>
        ) : null}
      </NavLink>
    </li>
  );
}
